import { Injectable } from '@angular/core';
import { DeliveryNoteBundleOverview } from '../../../api/models/delivery-note-bundle-overview';
import { ConsigneeDeliveryNoteInPool } from '../../../api/models/consignee-delivery-note-in-pool';
import { lastValueFrom, ReplaySubject, Subject, takeUntil, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoginOrganizationSite } from '../../../api/models/login-organization-site';
import { UserRoles } from '../../../api/models/user-roles';
import { DeliveryNoteBundlesService } from '../../../api/services/delivery-note-bundles.service';
import { GetConsigneeTours } from '../../../api/models/get-consignee-tours';
import { DeliveryNotesService } from '../../../api/services/delivery-notes.service';
import { ToursService } from '../../../api/services/tours.service';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';

export interface DeliveryNoteBundleWithOrgSiteKey
    extends DeliveryNoteBundleOverview {
    orgSiteKey: string;
    read_only: boolean;
}

export interface DeliveryNoteWithOrgSiteKey
    extends ConsigneeDeliveryNoteInPool {
    orgSiteKey: string;
    read_only: boolean;
}

export type SingleTour = GetConsigneeTours[0];
export interface TourWithOrgSiteKey extends SingleTour {
    orgSiteKey: string;
    read_only: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ConsigneeService {
    private _allSites: LoginOrganizationSite[] = [];
    private _selectedOrgSites: LoginOrganizationSite[] = [];
    private _currentOrgSiteKey: string = '';

    /**
     * Bundles
     */
    public deliveryNoteBundlesReplaySubject: ReplaySubject<
        DeliveryNoteBundleWithOrgSiteKey[]
    > = new ReplaySubject<DeliveryNoteBundleWithOrgSiteKey[]>(1);
    private deliveryNoteBundles: DeliveryNoteBundleWithOrgSiteKey[] = [];

    /**
     * Tours
     */
    public toursReplaySubject: ReplaySubject<TourWithOrgSiteKey[]> =
        new ReplaySubject<TourWithOrgSiteKey[]>(1);
    private tours: TourWithOrgSiteKey[] = [];

    /**
     * Delivery notes
     */
    public deliveryNotesReplaySubject: ReplaySubject<
        DeliveryNoteWithOrgSiteKey[]
    > = new ReplaySubject<DeliveryNoteWithOrgSiteKey[]>(1);
    private deliveryNotes: DeliveryNoteWithOrgSiteKey[] = [];

    private destroy$ = new Subject<void>();

    constructor(
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private toursService: ToursService,
        private deliveryNotesService: DeliveryNotesService,
        private storageService: StorageService
    ) {}

    /**
     * load bundles
     */
    async loadDeliveryNoteBundleOverviewsForSite(
        orgSiteKey?: string
    ): Promise<void> {
        // Get current organization site key from app store if not provided
        const { getCurrentOrganizationSiteKey } = await import(
            '@app/stores/appStore'
        );
        const targetOrgSiteKey = orgSiteKey || getCurrentOrganizationSiteKey();

        if (!targetOrgSiteKey) {
            console.warn('No organization site key available');
            return;
        }

        console.log(
            'ConsigneeService: loadDeliveryNoteBundleOverviewsForSite called with orgSiteKey:',
            targetOrgSiteKey
        );
        const { setFoldersLoading, setFoldersError, setFolders } = await import(
            '@app/stores/foldersStore'
        );

        setFoldersLoading(true);
        setFoldersError(null);

        const allFolders: import('@app/shared/models/store-types').FolderData[] =
            [];

        try {
            // Get site information from app store if not in selectedOrgSites
            const { getCurrentOrganizationSite } = await import(
                '@app/stores/appStore'
            );
            const currentSite = getCurrentOrganizationSite();
            const site =
                this._selectedOrgSites.find(s => s._key === targetOrgSiteKey) ||
                currentSite;

            const bundleOverviews = await lastValueFrom(
                this.deliveryNoteBundlesService
                    .getConsigneeBundles({
                        organizationSiteKey: targetOrgSiteKey,
                    })
                    .pipe(
                        takeUntil(this.destroy$),
                        catchError(() => {
                            // Silently handle cancellation
                            return EMPTY;
                        })
                    ),
                { defaultValue: [] }
            );

            bundleOverviews.forEach(overview => {
                const folderData: import('@app/shared/models/store-types').FolderData =
                    {
                        orgSiteKey: targetOrgSiteKey,
                        read_only: site
                            ? site.roles.includes(UserRoles.RemoteConsignee) &&
                              !site.roles.includes(UserRoles.Consignee)
                            : false,
                        ...overview,
                    };
                allFolders.push(folderData);
            });

            // Update the store with the specific site's data
            setFolders(allFolders);
            setFoldersLoading(false);

            // Also update the legacy ReplaySubject for backward compatibility
            this.deliveryNoteBundles = allFolders as any;
            this.deliveryNoteBundlesReplaySubject.next(
                this.deliveryNoteBundles
            );
        } catch (error: any) {
            // Ignore errors from cancelled requests
            if (error?.name !== 'EmptyError') {
                // Extract request ID from error if available
                const requestId =
                    error?.headers?.get?.('x-request-id') ||
                    error?.error?.requestId ||
                    null;
                const errorMessage = requestId
                    ? `GENERAL.ERRORS.FAILED_TO_LOAD_FOLDERS|${requestId}`
                    : 'GENERAL.ERRORS.FAILED_TO_LOAD_FOLDERS';
                setFoldersError(errorMessage);
                setFoldersLoading(false);
                throw error;
            }
        }
    }

    /**
     * load tours
     */
    async loadToursForSite(orgSiteKey?: string): Promise<void> {
        // Get current organization site key from app store if not provided
        const { getCurrentOrganizationSiteKey } = await import(
            '@app/stores/appStore'
        );
        const targetOrgSiteKey = orgSiteKey || getCurrentOrganizationSiteKey();

        if (!targetOrgSiteKey) {
            console.warn('No organization site key available');
            return;
        }

        console.log(
            'ConsigneeService: loadToursForSite called with orgSiteKey:',
            targetOrgSiteKey
        );
        const { setToursLoading, setToursError, setTours } = await import(
            '@app/stores/toursStore'
        );

        setToursLoading(true);
        setToursError(null);

        const allTours: import('@app/shared/models/store-types').TourData[] =
            [];

        try {
            // Get site information from app store if not in selectedOrgSites
            const { getCurrentOrganizationSite } = await import(
                '@app/stores/appStore'
            );
            const currentSite = getCurrentOrganizationSite();
            const site =
                this._selectedOrgSites.find(s => s._key === targetOrgSiteKey) ||
                currentSite;

            const tours = await lastValueFrom(
                this.toursService
                    .getConsigneeTours({
                        organizationSiteKey: targetOrgSiteKey,
                    })
                    .pipe(
                        takeUntil(this.destroy$),
                        catchError(() => {
                            // Silently handle cancellation
                            return EMPTY;
                        })
                    ),
                { defaultValue: [] }
            );

            tours.forEach(tour => {
                const tourData: import('@app/shared/models/store-types').TourData =
                    {
                        orgSiteKey: targetOrgSiteKey,
                        read_only: site
                            ? site.roles.includes(UserRoles.RemoteConsignee) &&
                              !site.roles.includes(UserRoles.Consignee)
                            : false,
                        ...tour,
                    };
                allTours.push(tourData);
            });

            // Update the store with the specific site's data
            setTours(allTours);
            setToursLoading(false);

            // Also update the legacy ReplaySubject for backward compatibility
            this.tours = allTours as any;
            this.toursReplaySubject.next(this.tours);
        } catch (error: any) {
            // Ignore errors from cancelled requests
            if (error?.name !== 'EmptyError') {
                // Extract request ID from error if available
                const requestId =
                    error?.headers?.get?.('x-request-id') ||
                    error?.error?.requestId ||
                    null;
                const errorMessage = requestId
                    ? `GENERAL.ERRORS.FAILED_TO_LOAD_TOURS|${requestId}`
                    : 'GENERAL.ERRORS.FAILED_TO_LOAD_TOURS';
                setToursError(errorMessage);
                setToursLoading(false);
                throw error;
            }
        }
    }

    /**
     * load delivery notes
     */
    async loadDeliveryNotesForSite(orgSiteKey?: string): Promise<void> {
        // Get current organization site key from app store if not provided
        const { getCurrentOrganizationSiteKey } = await import(
            '@app/stores/appStore'
        );
        const targetOrgSiteKey = orgSiteKey || getCurrentOrganizationSiteKey();

        if (!targetOrgSiteKey) {
            console.warn('No organization site key available');
            return;
        }

        console.log(
            'ConsigneeService: loadDeliveryNotesForSite called with orgSiteKey:',
            targetOrgSiteKey
        );
        const { setLoading, setError, setDeliveryNotes } = await import(
            '@app/stores/deliveryNotesStore'
        );

        setLoading(true);
        setError(null);

        const allNotes: import('@app/shared/models/store-types').DeliveryNoteData[] =
            [];

        try {
            // Get site information from app store if not in selectedOrgSites
            const { getCurrentOrganizationSite } = await import(
                '@app/stores/appStore'
            );
            const currentSite = getCurrentOrganizationSite();
            const site =
                this._selectedOrgSites.find(s => s._key === targetOrgSiteKey) ||
                currentSite;

            const notes = await lastValueFrom(
                this.deliveryNotesService
                    .getConsigneeDeliveryNotesMetadataInPool({
                        organizationSiteKey: targetOrgSiteKey,
                    })
                    .pipe(
                        takeUntil(this.destroy$),
                        catchError(() => {
                            // Silently handle cancellation
                            return EMPTY;
                        })
                    ),
                { defaultValue: [] }
            );

            notes.forEach(note => {
                const noteData: import('@app/shared/models/store-types').DeliveryNoteData =
                    {
                        orgSiteKey: targetOrgSiteKey,
                        read_only: site
                            ? site.roles.includes(UserRoles.RemoteConsignee) &&
                              !site.roles.includes(UserRoles.Consignee)
                            : false,
                        ...note,
                    };
                allNotes.push(noteData);
            });

            // Update the store with the specific site's data
            setDeliveryNotes(allNotes);
            setLoading(false);

            // Also update the legacy ReplaySubject for backward compatibility
            this.deliveryNotes = allNotes as any;
            this.deliveryNotesReplaySubject.next(this.deliveryNotes);
        } catch (error: any) {
            // Ignore errors from cancelled requests
            if (error?.name !== 'EmptyError') {
                // Extract request ID from error if available
                const requestId =
                    error?.headers?.get?.('x-request-id') ||
                    error?.error?.requestId ||
                    null;
                const errorMessage = requestId
                    ? `GENERAL.ERRORS.FAILED_TO_LOAD_DELIVERY_NOTES|${requestId}`
                    : 'GENERAL.ERRORS.FAILED_TO_LOAD_DELIVERY_NOTES';
                setError(errorMessage);
                setLoading(false);
                throw error;
            }
        }
    }

    castDeliveryNoteBundleOverviews(
        bundles: DeliveryNoteBundleOverview[],
        site: LoginOrganizationSite
    ): DeliveryNoteBundleWithOrgSiteKey[] {
        if (bundles && site) {
            let bundlesWithOrgSiteKey: DeliveryNoteBundleWithOrgSiteKey[] = [];
            for (const bundle of bundles) {
                bundlesWithOrgSiteKey.push({
                    orgSiteKey: site._key,
                    read_only:
                        site.roles.includes(UserRoles.RemoteConsignee) &&
                        !site.roles.includes(UserRoles.Consignee),
                    ...bundle,
                });
            }

            return bundlesWithOrgSiteKey;
        }
        return [];
    }

    castDeliveryNotes(
        notes: ConsigneeDeliveryNoteInPool[],
        site: LoginOrganizationSite
    ): DeliveryNoteWithOrgSiteKey[] {
        if (notes && site) {
            let notesWithOrgSiteKey: DeliveryNoteWithOrgSiteKey[] = [];
            for (const note of notes) {
                notesWithOrgSiteKey.push({
                    orgSiteKey: site._key,
                    read_only:
                        site.roles.includes(UserRoles.RemoteConsignee) &&
                        !site.roles.includes(UserRoles.Consignee),
                    ...note,
                });
            }

            return notesWithOrgSiteKey;
        }
        return [];
    }

    saveSitesSelection(sites: LoginOrganizationSite[]) {
        console.log('saving sites: ', sites);
        this._selectedOrgSites = sites;
        this.storageService.setItem(
            'consigneeSiteSelection',
            sites.map(site => site._key)
        );
    }

    loadSitesSelection(): LoginOrganizationSite[] {
        const sites: string[] = this.storageService.getItemAsObject(
            'consigneeSiteSelection'
        );
        if (this._allSites && sites) {
            this._selectedOrgSites = [];
            for (const siteId of sites) {
                const site = this._allSites.find(
                    orgSite => orgSite._key === siteId
                );
                if (site) {
                    this._selectedOrgSites.push(site);
                }
            }
        }
        return this._selectedOrgSites;
    }

    saveReferencedSiteKey(siteKey: string) {
        this.storageService.setItem('referencedSiteKeyConsignee', siteKey);
    }

    loadReferencedSiteKey(): void {
        this._currentOrgSiteKey = this.storageService.getItemAsObject(
            'referencedSiteKeyConsignee'
        );
    }

    isContentVisible(siteId: string): boolean {
        if (siteId) {
            return this._selectedOrgSites.some(site => site._key === siteId);
        }
        return false;
    }

    /**
     * getter setter
     */
    get selectedOrgSites(): string[] {
        return this._selectedOrgSites.map(site => site._key);
    }

    set allSites(value: LoginOrganizationSite[]) {
        this._allSites = value;
    }

    get allSites(): LoginOrganizationSite[] {
        return this._allSites;
    }

    get currentOrgSiteKey(): string {
        return this._currentOrgSiteKey;
    }

    set currentOrgSiteKey(value: string) {
        this._currentOrgSiteKey = value;
        this.saveReferencedSiteKey(value);
    }

    /**
     * Cancel all active HTTP requests
     */
    cancelActiveRequests(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.destroy$ = new Subject<void>();
    }
}
